import React from "react";
import { connect } from "react-redux";

import "./ActiveToolContainer.css";
import Tools from "../../components/Tools";

class ActiveToolContainer extends React.Component {
  constructor(props) {
    super(props);
    this.toolsRef = React.createRef();
    this.toolsInnerRef = React.createRef();
    this.hintTimeout = null;
    this.hasShownHintOnce = false;
    this.state = {
      isDragging: false,
      startY: 0,
      startHeight: 0,
      isScrollable: false,
      showScrollHint: false,
      dragStartedInHandle: false,
      isMobile: window.innerWidth <= 768
    };
  }

  componentDidMount() {
    // Set up scroll detection
    if (this.toolsInnerRef.current) {
      this.checkScrollable();
      this.toolsInnerRef.current.addEventListener("scroll", this.handleScroll);
    }
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    // Clean up scroll listener
    if (this.toolsInnerRef.current) {
      this.toolsInnerRef.current.removeEventListener(
        "scroll",
        this.handleScroll
      );
    }
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
    }
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.setState({ isMobile: window.innerWidth <= 768 });
  };

  componentDidUpdate(prevProps) {
    // Check if we're on a mobile device
    const isMobile = window.innerWidth <= 768;

    // Check for state inconsistency and fix it
    const { activeToolCollection, mobileDrawer } = this.props;

    // If the drawer state and tool visibility state are inconsistent, sync them
    if (activeToolCollection.isVisible !== mobileDrawer.isOpen) {
      // No need to update here as the useEffect in MobileToolCarousel will handle it
    }

    // Only handle on mobile devices
    if (isMobile) {
      const { activeToolCollection: prevActiveToolCollection } = prevProps;
      const { mobileDrawer: prevMobileDrawer } = prevProps;

      // More direct approach: always reset scroll if the active tool has changed
      if (
        activeToolCollection.activeTool !==
          prevActiveToolCollection.activeTool &&
        activeToolCollection.isVisible
      ) {
        // Immediately reset scroll
        this.resetScrollPosition();

        // And reset again after a delay to ensure content is loaded
        setTimeout(() => {
          this.resetScrollPosition();
        }, 100);
      }

      // We need to check both activeToolCollection visibility and mobileDrawer state
      // to handle all cases of drawer visibility changes
      const drawerWasOpened =
        (!prevActiveToolCollection.isVisible &&
          activeToolCollection.isVisible) ||
        (!prevMobileDrawer.isOpen && mobileDrawer.isOpen);

      const drawerWasClosed =
        (prevActiveToolCollection.isVisible &&
          !activeToolCollection.isVisible) ||
        (prevMobileDrawer.isOpen && !mobileDrawer.isOpen);

      // Handle drawer visibility changes
      if (drawerWasOpened || drawerWasClosed) {
        // Hide Beacon when drawer is open, show it when closed
        this.updateBeaconVisibility(mobileDrawer.isOpen);

        // If drawer was opened, reset height to 33%
        if (drawerWasOpened && this.toolsRef.current) {
          this.toolsRef.current.style.setProperty("--drawer-height", "33%");
          this.resetScrollPosition();
        }

        // Update classes on canvas-container regardless of how the drawer was opened/closed
        const canvasContainer = document.querySelector(".canvas-container");
        if (canvasContainer) {
          if (mobileDrawer.isOpen) {
            canvasContainer.classList.add("tools-active");
          } else {
            canvasContainer.classList.remove("tools-active");
          }
        }
      }
    }
  }

  checkScrollable = () => {
    if (this.toolsInnerRef.current) {
      // Check if content is actually scrollable
      const isScrollable =
        this.toolsInnerRef.current.scrollHeight >
        this.toolsInnerRef.current.clientHeight + 5; // Smaller threshold

      // Update isScrollable state but don't change hint visibility
      // (hint visibility is managed by resetScrollPosition)
      this.setState({ isScrollable });
    }
  };

  handleScroll = () => {
    if (this.toolsInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        this.toolsInnerRef.current;
      // Hide scroll indicator when near the bottom
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
      this.toolsInnerRef.current.classList.toggle(
        "is-scrollable",
        !isNearBottom && this.state.isScrollable
      );
    }
  };

  handleTouchStart = (e) => {
    // Get the touch point relative to the top of the tools container
    const touch = e.touches[0];
    const rect = this.toolsRef.current.getBoundingClientRect();
    const touchY = touch.clientY - rect.top;

    // Only initiate drag if touch starts in the handle area (top 40px)
    const dragStartedInHandle = touchY <= 40;

    this.setState({
      dragStartedInHandle,
      isDragging: dragStartedInHandle,
      startY: touch.clientY,
      startHeight: rect.height
    });

    if (dragStartedInHandle) {
      this.toolsRef.current.classList.add("dragging");
    }
  };

  handleTouchMove = (e) => {
    // Only handle drag if it started in the handle area
    if (!this.state.isDragging || !this.state.dragStartedInHandle) return;

    const touch = e.touches[0];
    const deltaY = this.state.startY - touch.clientY;
    const newHeight = this.state.startHeight + deltaY * 3;
    const windowHeight = window.innerHeight;

    // Allow height to go to 0% for closing
    const minHeight = 0;
    const maxHeight = windowHeight;
    const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

    // Convert to percentage
    const heightPercentage = (clampedHeight / windowHeight) * 100;

    this.toolsRef.current.style.setProperty(
      "--drawer-height",
      `${heightPercentage}%`
    );
  };

  handleTouchEnd = () => {
    if (!this.state.isDragging) return;

    this.setState({ isDragging: false, dragStartedInHandle: false });
    this.toolsRef.current.classList.remove("dragging");

    // Snap to either 0% (closed), 33% (default), or 100% (full) based on current height
    const rect = this.toolsRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const currentHeightPercentage = (rect.height / windowHeight) * 100;

    let snapHeight;
    if (currentHeightPercentage < 15) {
      // Close the drawer if dragged below 15%
      snapHeight = 0;
      // Hide the drawer using the existing mechanism
      if (this.props.activeToolCollection.isVisible) {
        // Find and click the active tool to close it
        const activeToolElement = document.querySelector(
          ".carousel-item.active"
        );
        if (activeToolElement) {
          activeToolElement.click();
        }
      }
    } else if (currentHeightPercentage > 85) {
      // Keep at full height only if very close to the top
      snapHeight = 100;
    } else {
      // Default height for everything else - makes it very easy to drag down from full height
      snapHeight = 33;
    }

    this.toolsRef.current.style.setProperty(
      "--drawer-height",
      `${snapHeight}%`
    );

    // After height change, check if content is scrollable
    this.checkScrollable();
  };

  // Helper method to reset scroll position and show scroll hint only first time
  resetScrollPosition = () => {
    if (this.toolsInnerRef.current) {
      // Use multiple scroll reset methods for maximum compatibility
      this.toolsInnerRef.current.scrollTo(0, 0);
      this.toolsInnerRef.current.scrollTop = 0;

      // Force layout recalculation
      void this.toolsInnerRef.current.offsetHeight;

      // Only show the hint if this is the first time a drawer has been opened
      if (!this.hasShownHintOnce) {
        this.setState({ showScrollHint: true });

        // Clear any existing timeout
        if (this.hintTimeout) {
          clearTimeout(this.hintTimeout);
        }

        // Hide the hint after 3 seconds
        this.hintTimeout = setTimeout(() => {
          this.setState({ showScrollHint: false });
        }, 3000);

        // Mark that we've shown the hint once
        this.hasShownHintOnce = true;
      }
    }
  };

  // Helper method to consistently update Beacon visibility
  updateBeaconVisibility = (isDrawerOpen) => {
    // Find the Beacon frame element
    const beaconFrame = document.querySelector(".BeaconFabButtonFrame");
    if (beaconFrame) {
      if (isDrawerOpen) {
        // Drawer opened - hide Beacon
        beaconFrame.style.opacity = "0";
        beaconFrame.style.visibility = "hidden";
      } else {
        // Drawer closed - show Beacon
        beaconFrame.style.opacity = "1";
        beaconFrame.style.visibility = "visible";
      }
    }
  };

  render() {
    if (!this.props.activeToolCollection) {
      return (
        <div className="tools" ref={this.toolsRef}>
          <h2>Select a Tool</h2>
        </div>
      );
    }

    // For mobile and desktop, use the mobileDrawer state from Redux
    const isToolsHidden = !this.props.mobileDrawer.isOpen;
    const isMobile = this.state.isMobile;

    const toolsClassName = `tools ${isToolsHidden ? "tools-hidden" : ""}`;
    const activeTool = this.props.activeToolCollection.activeTool;

    // Apply tools-active class to the parent container
    // Important: this affects the canvas positioning
    const canvasContainer = document.querySelector(".canvas-container");
    if (canvasContainer) {
      if (!isToolsHidden) {
        canvasContainer.classList.add("tools-active");
      } else {
        canvasContainer.classList.remove("tools-active");
      }
    }

    return (
      <div
        className={toolsClassName}
        ref={this.toolsRef}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
      >
        <div className="tools__inner" ref={this.toolsInnerRef}>
          <Tools
            key={activeTool}
            activeTool={activeTool}
            routeId={this.props.routeId}
          />
          <div
            className={`scroll-hint ${this.state.showScrollHint ? "visible" : ""}`}
          >
            Scroll for more options
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    activeToolCollection: state.activeToolCollection,
    mobileDrawer: state.mobileDrawer
  };
}

export default connect(mapStateToProps)(ActiveToolContainer);
